<template>
  <div
    v-if="availableLanguages.length > 1"
    class="cn-language-switcher relative"
    :class="{
      'cn-language-switcher--dark': dark,
    }"
  >
    <button
      class="cn-language-switcher__button flex items-center"
      aria-label="search"
      @click="toggle"
    >
      <CdnImg
        v-if="IS_SHOWN_FLAGS"
        :src="getFlagIcon(currentLanguage?.code)"
        :width="24"
        :height="17"
        format="webp"
        class="mr-2"
        :alt="currentLanguage?.code"
      />
      <span class="cn-language-switcher__button-text">
        <span class="text-sm">
          {{ $t('language') }} {{ currentLanguage?.code?.toUpperCase() }}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          class="inline"
          :class="{ 'rotate-180': isOpen }"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.707 9L4.06 5.354L3.707 5L3 5.707L3.353 6.061L7.176 9.885L7.706 10.415L8.059 10.062L12.059 6.062L12.414 5.708L11.707 5L11.354 5.354L7.706 9H7.707Z"
          />
        </svg>
      </span>
    </button>
    <transition
      enter-active-class="transform duration-300"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transform duration-300"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isOpen"
        class="cn-language-switcher__dropdown fixed bottom-0 left-0 right-0 top-0 flex w-screen items-end backdrop-blur-sm sm:absolute sm:bottom-auto sm:left-auto sm:top-full sm:mt-2 sm:w-56"
      >
        <div
          ref="target"
          class="cn-language-switcher__dropdown-block max-h-88 w-full overflow-y-auto py-2 shadow-lg"
        >
          <component
            :is="language.code !== currentLanguage?.code ? 'a' : 'div'"
            v-for="(language, i) in availableLanguages"
            :key="i"
            class="cn-language-switcher__language flex w-full items-center px-6 py-3"
            :href="
              language.code !== currentLanguage?.code
                ? switchLocalePath(language.code)
                : undefined
            "
            v-on="
              language.code === currentLanguage?.code ? { click: close } : {}
            "
          >
            <CdnImg
              v-if="IS_SHOWN_FLAGS"
              :src="getFlagIcon(language.code)"
              :width="24"
              :height="17"
              format="webp"
              class="mr-3"
            />
            <span class="text-sm">
              {{ language.code.toUpperCase() }}
            </span>
            <svg
              v-if="language.code === currentLanguage?.code"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="ml-auto"
            >
              <path
                d="M9.99997 15.586L6.70697 12.293L5.29297 13.707L9.99997 18.414L19.707 8.70697L18.293 7.29297L9.99997 15.586Z"
                fill="currentColor"
              />
            </svg>
          </component>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  onClickOutside,
  useBreakpoints,
  breakpointsTailwind,
} from '@vueuse/core';
import {
  useCountryByLang,
  useLanguages,
  useBodyScrollLock,
} from '@/composables';
import { CdnImg } from '@webplatform/cdn-image';

interface Props {
  dark?: boolean;
}

defineProps<Props>();
const IS_SHOWN_FLAGS = false;
const breakpoints = useBreakpoints(breakpointsTailwind);
const isDesktop = breakpoints.greater('xl');

const isOpen = ref(false);
useBodyScrollLock(() => isOpen.value && !isDesktop.value);
const { languages, currentLanguage } = useLanguages();
const { locales } = useI18n();

const switchLocalePath = useSwitchLocalePath();

const envLocalesSet = new Set(
  locales.value.map((locale) => {
    if (typeof locale === 'string') {
      return locale;
    }
    return locale.code;
  }),
);
const availableLanguages = computed(() => {
  return languages.value.filter((language) => envLocalesSet.has(language.code));
});

const close = () => {
  isOpen.value = false;
};
const toggle = () => {
  isOpen.value = !isOpen.value;
};

const target = ref(null);

onClickOutside(target, () => {
  close();
});

const getFlagIcon = (code: string | undefined) => {
  return `/svg/flags/${code ? useCountryByLang(code)?.toUpperCase() : ''}.svg`;
};

defineExpose({
  close,
  toggle,
});
</script>

<style lang="scss">
.cn-language-switcher {
  --ab-canyon-language-switcher-button-text-color-text: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-language-switcher-dropdown-color-bg: var(--ab-canyon-color-dark);
  --ab-canyon-language-switcher-dropdown-block-color-bg: var(
    --ab-canyon-color-light
  );
  --ab-canyon-language-switcher-language-color-bg-hover: var(
    --ab-canyon-color-neutral-light
  );
  --ab-canyon-language-switcher-language-icon-color-text: var(
    --ab-canyon-color-accent
  );

  --ab-canyon-language-switcher-button-text-color-text-dark: var(
    --ab-canyon-color-light
  );

  &__button-text {
    color: var(--ab-canyon-language-switcher-button-text-color-text);
  }
  &__dropdown {
    background-color: color-mix(
      in srgb,
      var(--ab-canyon-language-switcher-dropdown-color-bg),
      transparent 50%
    );
    z-index: 11;
  }
  &__dropdown-block {
    background-color: var(
      --ab-canyon-language-switcher-dropdown-block-color-bg
    );
  }
  &__language {
    &:hover {
      background-color: var(
        --ab-canyon-language-switcher-language-color-bg-hover
      );
    }

    svg {
      color: var(--ab-canyon-language-switcher-language-icon-color-text);
    }
  }
  &--dark {
    .cn-language-switcher {
      &__button-text {
        color: var(--ab-canyon-language-switcher-button-text-color-text-dark);
      }
    }
  }
}
</style>
